import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AssessmentIcon from '@mui/icons-material/Assessment';
import BugReportIcon from '@mui/icons-material/BugReport';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import MessageIcon from '@mui/icons-material/Message';
import StorageIcon from '@mui/icons-material/Storage';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
	Box,
	Card,
	CardContent,
	Checkbox,
	FormControlLabel,
	Grid,
	Tooltip,
	Typography,
} from '@mui/material';
import { differenceInSeconds, format } from 'date-fns';
import { formatDateOnly } from '../../Utils/date';
import { fr } from 'date-fns/locale';
import React, { useEffect, useState } from 'react';
import {
	Button,
	Datagrid,
	FunctionField,
	Link,
	List,
	TextField as RaTextField,
	Show,
	TopToolbar,
	useRecordContext,
	useRefresh,
} from 'react-admin';
import Loader from '../../Components/Loader';
import {
	CONNECTOR_PROCESS_PENDING,
	CONNECTOR_PROCESS_START,
	CONNECTOR_PROCESS_STOP_FORCE,
	CONNECTOR_PROCESS_STOP_WITH_ERROR,
	CONNECTOR_PROCESS_STOP_WITH_NO_ERROR,
	connectorAccountProcessStatus,
	forceStop,
	IConnectorAccountProcess,
	reload,
	resolutionStatusLst,
} from '../../Services/connectorAccountProcess';
import { ResolutionModal } from './Modals/ResolutionModal';
import { TestAnalysisModal } from './Modals/TestAnalysisModal';

const formatDuration = (seconds: number): string => {
	const h = Math.floor(seconds / 3600)
		.toString()
		.padStart(2, '0');
	const m = Math.floor((seconds % 3600) / 60)
		.toString()
		.padStart(2, '0');
	const s = Math.floor(seconds % 60)
		.toString()
		.padStart(2, '0');
	return `${h}:${m}:${s}`;
};

// Mirrors quanti-sdk model's ConnectorsAccountProcess.HasWarnings(): a process
// completed successfully but some requests hit a source-reported account
// limitation (e.g. Axeptio subscription plan not covering an endpoint).
const hasWarnings = (status: number, nbrRequestsWithWarning?: number | null): boolean =>
	status === CONNECTOR_PROCESS_STOP_WITH_NO_ERROR && (nbrRequestsWithWarning ?? 0) > 0;

const getStatusName = (
	status: number,
	resolutionStatus: number,
	nbrRequestsWithWarning?: number | null
) => {
	if (hasWarnings(status, nbrRequestsWithWarning)) return 'Success with warnings';

	let statusObject = connectorAccountProcessStatus.find(
		(item) => item.id === status
	);

	if (status === CONNECTOR_PROCESS_STOP_WITH_ERROR) {
		statusObject = resolutionStatusLst.find(
			(item) => item.id === resolutionStatus
		);
	}

	return statusObject ? statusObject.name : 'Unknown status';
};

const getStatusColor = (
	status: number,
	resolutionStatus: number,
	nbrRequestsWithWarning?: number | null
): string => {
	if (hasWarnings(status, nbrRequestsWithWarning)) return '#f59e0b';

	let statusObject = connectorAccountProcessStatus.find(
		(item) => item.id === status
	);

	if (status === CONNECTOR_PROCESS_STOP_WITH_ERROR) {
		statusObject = resolutionStatusLst.find(
			(item) => item.id === resolutionStatus
		);
	}

	return statusObject?.color ?? 'black';
};

const LogActions = () => {
	const record = useRecordContext<IConnectorAccountProcess>();
	const [openResolutionModal, setOpenResolutionModal] = useState(false);

	const refresh = useRefresh();

	if (!record) return null;

	const handleOpenResolutionModal = async () => {
		setOpenResolutionModal(true);
	};

	const handleOpenProject = () => {
		const { projectFolderId, skuParent, sku, accountId } = record;

		const w: any = window;

		const url = `${w.FRONT_URL}/${projectFolderId}/${skuParent}/${sku}/details/${accountId}`;
		window.open(url, '_blank');
	};

	const handleOpenIssue = () => {
		const url = `https://quantiio.atlassian.net/jira/software/c/projects/QV2/boards/7/issues`;
		window.open(url, '_blank');
	};

	return (
		<>
			<TopToolbar>
				<Button label="Voir le projet" onClick={handleOpenProject}>
					<VisibilityIcon />
				</Button>
				<Button label="Créer un ticket" onClick={handleOpenIssue}>
					<BugReportIcon />
				</Button>
				<Button label="Résolution" onClick={handleOpenResolutionModal}>
					<MessageIcon />
				</Button>
			</TopToolbar>
			<ResolutionModal
				open={openResolutionModal}
				handleClose={() => setOpenResolutionModal(false)}
				handleSuccess={() => {
					setOpenResolutionModal(false);
					refresh();
				}}
				process={record}
			/>
		</>
	);
};

export const LogView = () => {
	return (
		<Show actions={<LogActions />}>
			<LogContent />
		</Show>
	);
};

export const LogContent = () => {
	const record = useRecordContext();
	const refresh = useRefresh();

	const [loading, setLoading] = useState(false);
	const [openTestAnalysisModal, setOpenTestAnalysisModal] = useState(false);

	useEffect(() => {
		if (record?.status !== CONNECTOR_PROCESS_START) return;

		const interval = setInterval(() => {
			refresh();
		}, 10_000);

		return () => clearInterval(interval);
	}, [record?.status, refresh]);

	if (!record) return null;

	const {
		id,
		sku,
		projectFolderId,
		processType,
		startedAt,
		endedAt,
		processStartDate,
		serviceId,
		serviceName,
		processEndDate,
		nbrOfActiveRows,
		resolutionMessage,
		nbrOfInsertedRows,
		status,
		project,
		resolutionStatus,
		accountId,
		nbrOfTry,
		nbrOfRequests,
		nbrRequestsProcessed,
		nbrRequestsToProcess,
		nbrRequestsWithError,
		errorMessage,
		errorDetails,
		testMode,
		connectorVersion,
		processorVersion,
		datawarehouseCost,
		datawarehouseCostCurrency,
		nbrRequestsWithWarning,
	} = record;

	const start = new Date(startedAt);
	const end = endedAt ? new Date(endedAt) : new Date();
	const durationSeconds = differenceInSeconds(end, start);
	const startedAtFormatted = format(start, "dd MMM yyyy 'à' HH:mm", {
		locale: fr,
	});

	const processStartFormatted = formatDateOnly(processStartDate);
	const processEndFormatted = formatDateOnly(processEndDate);

	const handleRefeshProcess = async () => {
		if (window.confirm('Êtes-vous sûr de vouloir relancer le process ?')) {
			setLoading(true);
			try {
				await reload(id.toString());
				refresh();
				alert('Process relancé avec succès !');
			} catch (error: any) {
				alert('Erreur lors de la relance du process : ' + error.message);

				return;
			}

			setLoading(false);
		}
	};

	const handleStopProcess = async () => {
		if (window.confirm('Êtes-vous sûr de vouloir stoper le process ?')) {
			setLoading(true);

			try {
				await forceStop(id.toString());
				refresh();
				alert('Process stoppé !');
			} catch (error: any) {
				alert('Erreur lors du stop du process : ' + error.message);
			}

			setLoading(false);
		}
	};

	const handleGraphanaLog = () => {
		const w: any = window;

		const jobs = serviceId.split('|');

		if (jobs.length > 1) {
			const url = `${w.GRAPHANA_JOB_URL.replace(
				'{serviceName}',
				serviceName.replace('_', '-')
			).replace('{jobRunId}', jobs[1])}`;

			window.open(url, '_blank');
		}
	};

	return (
		<Show>
			<Loader show={loading} />
			<Box p={2}>
				<Card variant="outlined" sx={{ mb: 2 }}>
					<CardContent>
						<Typography variant="h6" gutterBottom>
							Informations du log
						</Typography>
						<Grid container spacing={2}>
							<Grid item xs={12} sm={6}>
								<b>ID :</b> {id}
							</Grid>
							<Grid item xs={12} sm={6}>
								<b>SKU :</b> {sku}
							</Grid>
							<Grid item xs={12} sm={6}>
								<b>Project Folder :</b>{' '}
								{project && project.id && (
									<Link to={`/projects/${project.id}`} target="_blank">
										{project.name} ({projectFolderId})
									</Link>
								)}
							</Grid>
							<Grid item xs={12} sm={6}>
								<b>Account Id :</b> {accountId}
							</Grid>
							{testMode && (
								<Grid item xs={12}>
									<Typography
										variant="body2"
										sx={{
											backgroundColor: 'warning.main',
											color: 'warning.contrastText',
											px: 1,
											py: 0.5,
											borderRadius: 1,
											display: 'inline-block',
											fontWeight: 'bold',
											mr: 2,
										}}
									>
										MODE TEST E2E
									</Typography>
									<Button
										label="Voir le dataset BigQuery"
										onClick={() => {
											const dbName = record.acc?.databaseName || accountId;
											const dataset = `e2e_${projectFolderId}_${sku}_${dbName}`;
											const url = `https://console.cloud.google.com/bigquery?project=quanti-manager-staging&d=${dataset}&p=quanti-manager-staging&page=dataset`;
											window.open(url, '_blank');
										}}
									>
										<StorageIcon />
									</Button>
									<Button
										label="Analyse test vs prod"
										onClick={() => setOpenTestAnalysisModal(true)}
									>
										<AssessmentIcon />
									</Button>
									<TestAnalysisModal
										open={openTestAnalysisModal}
										handleClose={() => setOpenTestAnalysisModal(false)}
										processId={id.toString()}
									/>
								</Grid>
							)}
							<Grid item xs={12} sm={6}>
								<b>Connector Version :</b> {connectorVersion || '—'}
							</Grid>
							<Grid item xs={12} sm={6}>
								<b>Processor Version :</b> {processorVersion || '—'}
							</Grid>
							<Grid item xs={12} sm={6}>
								<b>Start Date :</b> {processStartFormatted}
							</Grid>
							<Grid item xs={12} sm={6}>
								<b>End Date :</b> {processEndFormatted}
							</Grid>
							<Grid item xs={12} sm={6}>
								<b>Process Type :</b> {processType}
							</Grid>
							<Grid item xs={12} sm={6}>
								<b>Status :</b>{' '}
								<span
									style={{
										color: getStatusColor(status, resolutionStatus, nbrRequestsWithWarning),
										fontWeight: 'bold',
									}}
								>
									{getStatusName(status, resolutionStatus, nbrRequestsWithWarning)}{' '}
									{(status === CONNECTOR_PROCESS_STOP_WITH_NO_ERROR ||
										status === CONNECTOR_PROCESS_STOP_WITH_ERROR ||
										status === CONNECTOR_PROCESS_STOP_FORCE) && (
										<Button
											label="Relancer"
											onClick={() => handleRefeshProcess()}
										/>
									)}
									{(status === CONNECTOR_PROCESS_START ||
										status === CONNECTOR_PROCESS_PENDING) && (
										<Button
											label="Force Stop"
											onClick={() => handleStopProcess()}
										/>
									)}
								</span>
							</Grid>
							<Grid item xs={12} sm={6}>
								<b>Démarré :</b> {startedAtFormatted}
							</Grid>
							<Grid item xs={12} sm={6}>
								<b>Nbr d'itération :</b> {nbrOfTry}
							</Grid>
							<Grid item xs={12} sm={6}>
								<b>Durée :</b> {formatDuration(durationSeconds)}
							</Grid>
							<Grid item xs={12} sm={6}>
								<b>Inserted rows :</b>{' '}
								{nbrOfActiveRows
									? nbrOfActiveRows.toLocaleString('en-US')
									: nbrOfInsertedRows.toLocaleString('en-US')}
							</Grid>
							<Grid item xs={12} sm={6}>
								<b>Config :</b>{' '}
								<Link
									to={`/connector-accounts/${projectFolderId}/${sku}/${accountId}/yml`}
									target="_blank"
								>
									Open
								</Link>
							</Grid>
							<Grid item xs={12} sm={6}>
								<b>Graphana logs :</b>{' '}
								<Button label="open" onClick={() => handleGraphanaLog()} />
							</Grid>
							<Grid item xs={12}>
								<b>Erreur :</b> {errorMessage || '—'}
							</Grid>
							<Grid item xs={12}>
								<b>Détails :</b> {errorDetails || '—'}
							</Grid>
							<Grid item xs={12}>
								<b>Résolution :</b> {resolutionMessage || '—'}
							</Grid>
						</Grid>
					</CardContent>
				</Card>

				<Card>
					<CardContent>
						<Typography variant="h6" gutterBottom>
							Statistiques
						</Typography>
						<Grid container spacing={2}>
							<Grid item xs={12} sm={6}>
								<b>Nombre de requêtes total :</b>{' '}
								{nbrOfRequests ? nbrOfRequests.toLocaleString('en-US') : 0}
							</Grid>
							<Grid item xs={12} sm={6}>
								<b>Nombre de requêtes traitées :</b>{' '}
								{nbrRequestsProcessed
									? nbrRequestsProcessed.toLocaleString('en-US')
									: 0}
							</Grid>
							<Grid item xs={12} sm={6}>
								<b>Nombre de requêtes à traiter :</b>{' '}
								{nbrRequestsToProcess
									? nbrRequestsToProcess.toLocaleString('en-US')
									: 0}
							</Grid>
							<Grid item xs={12} sm={6}>
								<b>Nombre de requêtes avec erreur :</b>{' '}
								{nbrRequestsWithError
									? nbrRequestsWithError.toLocaleString('en-US')
									: 0}
							</Grid>
							<Grid item xs={12} sm={6}>
								<b>Coût Datawarehouse :</b>{' '}
								{datawarehouseCost
									? `$${datawarehouseCost.toFixed(4)} ${datawarehouseCostCurrency || 'USD'}`
									: '—'}
							</Grid>
						</Grid>
					</CardContent>
				</Card>

				<Box mt={4}>
					<LogRowList />
				</Box>
			</Box>
		</Show>
	);
};

const LogRowList = () => {
	const [errorOnly, setErrorOnly] = useState(false);
	const record = useRecordContext();
	const [openResolutionModal, setOpenResolutionModal] = useState(false);
	const [selectedProcess] = useState<IConnectorAccountProcess | null>(null);
	const refresh = useRefresh();

	if (!record) return null;
	const handleToggle = () => {
		setErrorOnly((prev) => !prev);
	};

	return (
		<>
			<FormControlLabel
				control={
					<Checkbox
						checked={errorOnly}
						onChange={handleToggle}
						color="primary"
					/>
				}
				label="Error only"
				sx={{ ml: 2, mt: 1 }}
			/>
			<List
				resource="log-rows"
				filter={{
					logId: record.id,
					...(errorOnly ? { errorMessage_ne: '' } : {}),
				}}
				title="Lignes du log"
				perPage={50}
				actions={false}
				sort={{ field: 'date', order: 'DESC' }}
			>
				<Datagrid>
					<StatusField />
					<RaTextField source="requestId" />
					<RaTextField source="adAccount" />
					<DateOrDimensionField />
					<FunctionField
						label="Nbr of insterted rows"
						render={(record) =>
							record.nbrOfActiveRows ?? record.nbrOfInsertedRows
						}
					/>
					<FunctionField
						label="Coût DW"
						render={(record) =>
							record.datawarehouseCost
								? `$${record.datawarehouseCost.toFixed(4)}`
								: '—'
						}
					/>
					<RaTextField source="errorMessage" />
					<RaTextField source="errorDetails" />
				</Datagrid>
			</List>
			<ResolutionModal
				open={openResolutionModal}
				handleClose={() => setOpenResolutionModal(false)}
				handleSuccess={() => {
					setOpenResolutionModal(false);
					refresh();
				}}
				process={selectedProcess}
			/>
		</>
	);
};

// quanti-sdk (errors.go) réserve les codes >= 3000 à la famille "WARN" :
// une limitation reportée par la source elle-même (ex : plan Axeptio ne
// couvrant pas ce endpoint), pas un vrai échec côté connecteur.
const ERR_CODE_WARN_THRESHOLD = 3000;

const StatusField = () => {
	const record = useRecordContext();
	if (!record) return null;

	let icon = null;

	switch (record.status) {
		case 200:
			icon = <CheckCircleIcon fontSize="small" color="success" />;
			break;
		case 0:
			icon = <AccessTimeIcon fontSize="small" color="warning" />;
			break;
		case 300:
			if (record.errorCode >= ERR_CODE_WARN_THRESHOLD) {
				icon = (
					<Tooltip title="Warning (limitation reportée par la source, pas un échec du connecteur)">
						<WarningAmberIcon fontSize="small" color="warning" />
					</Tooltip>
				);
			} else {
				icon = <ErrorIcon fontSize="small" color="error" />;
			}
			break;
		default:
			icon = <ErrorIcon fontSize="small" color="error" />;
			break;
	}

	return (
		<Box display="flex" justifyContent="center" alignItems="center">
			{icon}
		</Box>
	);
};

StatusField.defaultProps = {
	label: 'Status',
};

const DateOrDimensionField = () => {
	const record = useRecordContext();
	if (!record) return null;
	return (
		<span style={{ fontStyle: record.isDimension ? 'italic' : 'normal' }}>
			{record.isDimension ? 'dimension' : record.date}
		</span>
	);
};

DateOrDimensionField.defaultProps = {
	label: 'Date',
};
